import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';

const ShopCartContext = createContext(null);
const STORAGE_KEY = 'bison_shop_cart_v1';

export const ShopCartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return prev.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        unit: product.unit,
        unit_price: product.final_price,
        image_url: product.image_url,
        quantity,
      }];
    });
  };

  const setQuantity = (productId, quantity) => {
    if (quantity <= 0) return removeItem(productId);
    setItems(prev => prev.map(i => i.product_id === productId ? { ...i, quantity } : i));
  };

  const removeItem = (productId) => setItems(prev => prev.filter(i => i.product_id !== productId));
  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <ShopCartContext.Provider value={{ items, addItem, setQuantity, removeItem, clear, subtotal, count }}>
      {children}
    </ShopCartContext.Provider>
  );
};

export const useShopCart = () => {
  const ctx = useContext(ShopCartContext);
  if (!ctx) throw new Error('useShopCart must be used inside ShopCartProvider');
  return ctx;
};
